"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useSession, signOut } from "next-auth/react";
import { Menu, X, LogOut, User, Shield } from "lucide-react";
import ThemeToggle from "@/components/common/ThemeToggle";

export default function Header() {
    const { data: session } = useSession();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);

    const navLinks = [
        { href: "/", label: "Strona główna" },
        { href: "/inby", label: "INBy" },
        { href: "/ranking", label: "Ranking" },
        { href: "/tier-lista", label: "Tier Lista" },
    ];

    const isAdmin = session?.user?.role === "ADMIN";

    // Required for portal to work with SSR
    useEffect(() => {
        setMounted(true);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileMenuOpen]);

    const closeMenu = () => setMobileMenuOpen(false);

    const MobileMenu = () => (
        <div className="mobile-menu-overlay" onClick={closeMenu}>
            <div
                className="mobile-menu-panel"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    className="absolute top-4 right-4 p-2 text-gray-700 hover:text-black z-10"
                    onClick={closeMenu}
                    aria-label="Zamknij menu"
                >
                    <X size={28} />
                </button>

                {/* Logo in mobile menu */}
                <div className="mb-8">
                    <span className="text-2xl font-bold text-[#6b90fc]">
                        INBOmetr
                    </span>
                </div>

                {/* Navigation links */}
                <nav className="flex flex-col items-center gap-5 w-full">
                    {navLinks.map((link) => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className="text-xl text-gray-700 hover:text-[#6b90fc] transition-colors py-2"
                            onClick={closeMenu}
                        >
                            {link.label}
                        </Link>
                    ))}

                    <div className="w-16 h-px bg-gray-200 my-2" />

                    {session ? (
                        <>
                            {isAdmin && (
                                <Link
                                    href="/admin"
                                    className="text-xl text-amber-500 hover:text-amber-600 transition-colors flex items-center gap-2 py-2"
                                    onClick={closeMenu}
                                >
                                    <Shield size={20} />
                                    Panel Admina
                                </Link>
                            )}
                            <div className="text-gray-500 text-sm py-2">
                                {session.user?.name || session.user?.email}
                            </div>
                            <button
                                onClick={() => {
                                    signOut();
                                    closeMenu();
                                }}
                                className="text-xl text-gray-700 hover:text-red-500 transition-colors flex items-center gap-2 py-2"
                            >
                                <LogOut size={20} />
                                Wyloguj
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                href="/login"
                                className="text-xl text-gray-700 hover:text-[#6b90fc] transition-colors py-2"
                                onClick={closeMenu}
                            >
                                Zaloguj się
                            </Link>
                            <Link
                                href="/register"
                                className="btn btn-primary text-lg px-6 py-2"
                                onClick={closeMenu}
                            >
                                Zarejestruj
                            </Link>
                        </>
                    )}
                </nav>
            </div>
        </div>
    );

    return (
        <>
            <header className="sticky top-0 z-40 w-full border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-900/80">
                <div className="container mx-auto px-4">
                    <div className="flex h-16 items-center justify-between">
                        {/* Logo */}
                        <Link href="/" className="flex items-center gap-2">
                            <div className="relative h-10 w-10 overflow-hidden rounded-full border border-gray-200 dark:border-gray-700">
                                <img
                                    src="/logo.jpg"
                                    alt="INBOmetr Logo"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <span className="text-2xl font-bold text-[#6b90fc]">
                                INBOmetr
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className="text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors"
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Auth buttons - Desktop */}
                        <div className="hidden md:flex items-center gap-4">
                            <ThemeToggle />
                            {session ? (
                                <div className="flex items-center gap-4">
                                    {isAdmin && (
                                        <Link
                                            href="/admin"
                                            className="flex items-center gap-2 text-amber-500 hover:text-amber-600 transition-colors"
                                        >
                                            <Shield size={18} />
                                            <span>Panel Admina</span>
                                        </Link>
                                    )}
                                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                        <User size={18} />
                                        <span>{session.user?.name || session.user?.email}</span>
                                    </div>
                                    <button
                                        onClick={() => signOut()}
                                        className="btn btn-ghost flex items-center gap-2"
                                    >
                                        <LogOut size={18} />
                                        <span>Wyloguj</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <Link href="/login" className="btn btn-ghost">
                                        Zaloguj się
                                    </Link>
                                    <Link href="/register" className="btn btn-primary">
                                        Zarejestruj
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            className="md:hidden p-2 text-gray-700 hover:text-black dark:text-gray-200 dark:hover:text-white"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label="Menu"
                        >
                            <Menu size={24} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Menu - Rendered via Portal */}
            {mounted && mobileMenuOpen && createPortal(
                <MobileMenu />,
                document.body
            )}
        </>
    );
}

